/**
 * The wire contract for copilots: who a pilot flies with, how they get asked,
 * and what travels between two accounts.
 *
 * Imported by both the PWA and the `crew` Lambda, for the same reason
 * `core/sync/protocol.ts` is: a hand-kept copy on each side drifts, and the
 * failure mode is an invitation that one build sends and the other cannot read.
 *
 * ## The model in one paragraph
 *
 * A **copilot** is another account this pilot has linked to, by invitation and
 * acceptance — never one-sided. Only a copilot can be sent a **share**, and a
 * share is a redacted snapshot (`core/transfer/snapshot.ts`, `kind: 'share'`)
 * of a few records: a flight plan with the aircraft and the aerodrome cards it
 * names, an aircraft with its checklists, or an aerodrome card. The recipient
 * previews it and imports it into their own account; nothing is ever written
 * into somebody else's account directly, and nothing is ever read out of one.
 *
 * ## What never crosses
 *
 * The sharing policy on the entity registry decides, not this file: pilot
 * profiles, the logbook, checklist runs, notes and saved documents (which carry
 * signatures) are `private` and are not in a bundle whatever was asked for. A
 * flight plan loses its pilot-in-command, filer, contact phone and pilot
 * reference. Preferences never travel to a copilot at all.
 */

/** Another account this pilot is linked with. */
export interface Copilot {
	userId: string
	email: string
	displayName: string | null
	/** When the link was made, ISO 8601. */
	since: string
}

export type InvitationStatus = 'pending' | 'accepted' | 'declined' | 'cancelled' | 'expired'

/** An invitation this pilot sent. */
export interface OutgoingInvitation {
	id: string
	/** The address it was sent to, as typed (lower-cased). */
	toEmail: string
	status: InvitationStatus
	createdAt: string
	/** After this the server removes it, ISO 8601. */
	expiresAt: string
}

/** An invitation this pilot received. */
export interface IncomingInvitation {
	id: string
	fromUserId: string
	fromEmail: string
	fromName: string | null
	createdAt: string
	expiresAt: string
}

/** What one shared bundle is about, so a list can be read without opening it. */
export type ShareKind = 'flightPlan' | 'aircraft' | 'checklist' | 'airfield' | 'note'

/** A share, as listed. The bundle itself is fetched separately — it can be large. */
export interface ShareSummary {
	id: string
	kind: ShareKind
	/** Human title: the registration, the checklist's name, the ICAO code, or the plan's route. */
	title: string
	/** Records per entity type inside the bundle. */
	counts: Record<string, number>
	approximateBytes: number
	createdAt: string
	expiresAt: string
}

/** A share this pilot received. */
export interface IncomingShare extends ShareSummary {
	fromUserId: string
	fromEmail: string
	fromName: string | null
}

/** A share this pilot sent. */
export interface OutgoingShare extends ShareSummary {
	toUserId: string
	toEmail: string
}

/** Everything `GET /api/crew` returns. */
export interface CrewState {
	copilots: Copilot[]
	incomingInvitations: IncomingInvitation[]
	outgoingInvitations: OutgoingInvitation[]
	incomingShares: IncomingShare[]
	outgoingShares: OutgoingShare[]
	serverTime: string
}

export interface InviteRequest {
	email: string
}

export interface InviteResponse {
	invitation: OutgoingInvitation
}

/**
 * `POST /api/crew/shares`.
 *
 * The bundle is a redacted snapshot built by the client (`redactForSharing`),
 * and the server checks the redaction again before storing it: a client build
 * that forgot would otherwise be enough to send a licence number across.
 */
export interface CreateShareRequest {
	toUserId: string
	kind: ShareKind
	title: string
	bundle: unknown
}

export interface CreateShareResponse {
	share: OutgoingShare
}

/** `GET /api/crew/shares/{id}` — the bundle, for preview and import. */
export interface ShareBundleResponse {
	share: IncomingShare
	bundle: unknown
}

/** Hard ceilings, enforced on the server and pre-checked on the client. */
export const CREW_LIMITS = {
	/**
	 * Per bundle. A flight plan with its aircraft, that aircraft's checklists and
	 * four aerodrome cards is well under 100 KB; the ceiling leaves room for a
	 * long checklist set while staying under DynamoDB's 400 KB item limit with
	 * the envelope around it.
	 */
	maxBundleBytes: 300_000,
	/** Invitations and shares are removed after this many days if nobody acts. */
	expiryDays: 30,
	/** Copilots per account. Generous for a club; a ceiling so a partition cannot be spammed. */
	maxCopilots: 50,
	/** Open invitations per account, in each direction. */
	maxPendingInvitations: 20,
	/** Pending shares per recipient. */
	maxPendingShares: 50,
} as const

export const SHARE_KINDS: readonly ShareKind[] = ['flightPlan', 'aircraft', 'checklist', 'airfield', 'note']

export function isShareKind(value: unknown): value is ShareKind {
	return typeof value === 'string' && (SHARE_KINDS as readonly string[]).includes(value)
}

/**
 * Address check, deliberately loose. Enough to catch a typo; an RFC 5322 regex
 * rejects addresses that work.
 */
export function isEmailAddress(value: string): boolean {
	return /^[^\s@]+@[^\s@.]+\.[^\s@]+$/.test(value.trim())
}

/** Lower-cased and trimmed: `Martin@` and `martin@` are one mailbox. */
export function normaliseEmail(value: string): string {
	return value.trim().toLowerCase()
}
